import {
	ChangeDetectorRef,
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChanges,
} from '@angular/core';
import {
	CarrierFeedback,
	CarrierNotes,
	CarrierNotesListResponse,
	GET_CARRIER_BUSINESS_NAME,
	RestService,
	User,
} from 'cargo-chief-commons';
import { NotesService } from '@service/notesService/notes.service';
import { UrlService } from '@service/url/url.service';
import { UtilService } from '@service/util/util.service';
import { SNACKBAR } from '@domain/constants/snackbar-setting';
import { takeUntil } from 'rxjs/operators';
import { BaseComponent } from '@shared/base/base.component';
import { FeedbackInformation } from '@notes/note/note.component';
import { Cargochief } from '@service/cargochief/cargochief.service';
import { CARGOCHIEF_KEYS } from '@domain/constants/app-variables';

export enum NoteType {
	CARRIER,
	PROJECT,
}

interface CarrierNoteData {
	active: boolean;
	base: CarrierNotes;
	feedback: any;
	noteEditAndDeleteEnabled: boolean;
	user: User;
}

@Component({
	selector: 'app-note-list',
	templateUrl: './note-list.component.html',
})
export class NoteListComponent extends BaseComponent implements OnInit, OnChanges, OnDestroy {
	@Input() noteType: NoteType = NoteType.CARRIER;
	@Input() id: string = null;
	@Input() supportId: string = null;
	@Input() name: string = null;
	@Input() feedbackInformation: FeedbackInformation;
	@Input() showFeedback: boolean;
	@Input() defaultFeedback: CarrierFeedback[] | null;
	@Input() feedbackClicked: EventEmitter<any> | null;
	@Input() noteCount: number;
	@Input() manualNoteCount = false;
	@Input() useExactMatch = true;
	@Input() edit = true;
	@Input() truckLaneId:string = null;
	@Input() origin:string = null;
	@Input() destination:string = null;

	@Output() noteCountUpdated: EventEmitter<number> = new EventEmitter<number>();
	@Output() noteAdded: EventEmitter<any> = new EventEmitter();

	page = 0;
	size = 100;
	isLoading: boolean;
	hasNext = true;

	carrierNotes: Map<string, CarrierNoteData> = new Map();
	hasActiveEdit: string | undefined | null = null;

	constructor(
		private ref: ChangeDetectorRef,
		private urlService: UrlService,
		private restService: RestService,
		private notesService: NotesService,
		private utilService: UtilService,
		private cargochief: Cargochief
	) {
		super();
	}

	ngOnInit(): void {
		if (this.showFeedback === undefined) {
			this.showFeedback = this.noteType === NoteType.CARRIER;
		}

		this.feedbackClicked?.pipe(takeUntil(this.isDestroyed$)).subscribe((id) => {
			if (id) {
				this.makeActive(id);
			}
		});

		if (this.defaultFeedback?.length) {
			this.loadFeedback(this.defaultFeedback, false);
		}
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes?.name?.previousValue !== changes?.name?.currentValue) {
			this.carrierNotes = new Map();
			this.hasActiveEdit = null;
			this.hasNext = true;
			this.page = 0;
		}

		if (this.defaultFeedback?.length) {
			this.loadFeedback(this.defaultFeedback, false);
		}

		if (changes?.id || changes?.supportId) {
			this.fetchNotes();
		}

		if (
			(changes?.id || changes?.supportId) &&
			this.noteType === NoteType.CARRIER &&
			this.id &&
			!this.name
		) {
			this.restService
				.POST(this.urlService.getCarriersByIds(), { carrierIDs: [this.id] })
				.pipe(takeUntil(this.isDestroyed$))
				.subscribe((result) => {
					const carrier = result.data.content[0];
					this.name = GET_CARRIER_BUSINESS_NAME(carrier?.businessName, carrier?.doingBusinessAs);
				});
		}
	}

	fetchNotes(): void {
		if (!this.hasNext) {
			return;
		}

		this.isLoading = true;

		const url = this.urlService.listCarrierNotesByPost(this.page, this.size);

		const payload = {
			projectId: this.noteType === NoteType.PROJECT ? this.id : '',
			carrierIds: this.noteType === NoteType.CARRIER ? [this.id] : [],
		};

		this.restService
			.POST(url, payload)
			.pipe(takeUntil(this.isDestroyed$))
			.subscribe(
				(response: CarrierNotesListResponse) => {
					this.isLoading = false;

					let users;

					try {
						users = JSON.parse(
							this.cargochief.getItem(CARGOCHIEF_KEYS.ACCOUNT_USERS)
						) as Array<User>;
					} catch (ignored) {}

					const mappedResponse = response.data.map(
						(data) =>
							<CarrierNoteData>{
								active: false,
								base: data,
								feedback: null,
								noteEditAndDeleteEnabled:
									this.utilService.isUserRoleAdminOrOwnerOnly() ||
									data.userId === this.cargochief.USER_ID,
								user: users?.find((user) => Number(user.id) === Number(data.userId)),
							}
					);

					const newMap = new Map();
					if (this.carrierNotes.size) {
						for (const [key, value] of this.carrierNotes.entries()) {
							newMap.set(key, value);
						}
					}

					mappedResponse.forEach((note) => {
						if (!this.supportId || this.checkSupportID(note.base)) {
							newMap.set(note.base.id, note);
						}
					});

					this.carrierNotes = newMap;
					if (this.defaultFeedback?.length) {
						this.loadFeedback(this.defaultFeedback, false);
					}
					this.hasNext = response.nextPage;

					if (!this.manualNoteCount) {
						this.noteCount = this.carrierNotes.size;
						this.noteCountUpdated.emit(this.noteCount);
					}
				},
				(error) => {
					this.isLoading = false;
					this.utilService.showSnackbar(
						error.data || 'Unable to load notes...',
						SNACKBAR.ERROR,
						SNACKBAR.ACTION
					);
				}
			);
	}

	addNote(note: CarrierNotes): void {
		const newMap = new Map();

		newMap.set(note.id, {
			active: false,
			base: note,
			feedback: null,
			noteEditAndDeleteEnabled: true,
		});

		for (const [key, value] of this.carrierNotes.entries()) {
			newMap.set(key, value);
		}

		this.carrierNotes = newMap;
		this.noteCount += 1;
		this.noteCountUpdated.emit(this.noteCount);
		if (this.defaultFeedback) {
			this.loadFeedback(this.defaultFeedback, false);
		}
		this.noteAdded.emit(note);
		this.hasActiveEdit = null;
	}

	deleteNote(noteId: string) {
		this.carrierNotes.delete(noteId);
		this.noteCount -= 1;
		this.noteCountUpdated.emit(this.noteCount);
	}

	loadFeedback(feedbacks: CarrierFeedback[], newFeedback: boolean): void {
		const newMap = new Map();

		if (newFeedback) {
			this.defaultFeedback.unshift(feedbacks[0]);
		}

		for (const [key, value] of this.carrierNotes.entries()) {
			newMap.set(key, {
				...value,
				feedback: feedbacks.find((feedback) => feedback.noteId === key) || value.feedback,
			});
		}

		this.carrierNotes = newMap;
	}

	makeActive(index: string | undefined | null): void {
		this.hasActiveEdit = index;

		const newMap = new Map();
		for (const [key, value] of this.carrierNotes.entries()) {
			newMap.set(key, {
				active: index === key,
				base: value.base,
				feedback: value.feedback,
				noteEditAndDeleteEnabled: value.noteEditAndDeleteEnabled,
			});
		}

		this.carrierNotes = newMap;
	}

	onScroll(): void {
		if (this.hasNext) {
			this.page++;
			this.fetchNotes();
		}
	}

	getCarrierNoteKeys(): string[] {
		return Array.from(this.carrierNotes.keys());
	}

	public getCarrierNotesList(): Array<CarrierNotes> {
		return Array.from(this.carrierNotes.values()).map((note) => note.base);
	}

	private checkSupportID(note: CarrierNotes): boolean {
		return this.noteType === NoteType.CARRIER
			? note.projectId === this.supportId || note.projectId === null
			: note.carrierId === this.supportId;
	}

	public isProjectType(): boolean {
		return this.noteType === NoteType.PROJECT;
	}
}
